import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { AccountType } from '~modules/auth/types/account.types';
import { UserType } from '~shared/enums/user.enum';

import { AuthService } from './auth.service';

const STORAGE_KEY = 'clientMode';

/**
 * Client mode lets a professional show the configurator to their own client: prices
 * are hidden and their logo replaces ours in the header. It is offered to an external
 * user of a PROFESSIONAL account, and to an internal user who switched to such an
 * account (to see what the professional sees). The switch is remembered in this
 * browser until logout, which clears localStorage.
 */
@Injectable({ providedIn: 'root' })
export class ClientModeService {
	private readonly enabled$ = new BehaviorSubject<boolean>(
		typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === 'true'
	);

	public readonly available$: Observable<boolean>;
	public readonly active$: Observable<boolean>;
	/** The professional's account, whose logo the header shows; null when not active. */
	public readonly accountId$: Observable<string | null>;

	constructor(private readonly authService: AuthService) {
		this.available$ = this.authService.currentUser$.pipe(
			map((user) =>
				user?.account?.accountType === AccountType.PROFESSIONAL
				&& (user.userType === UserType.EXTERNAL || !!user.account.overwritten)
			),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.active$ = combineLatest([this.available$, this.enabled$]).pipe(
			map(([available, enabled]) => available && enabled),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.accountId$ = combineLatest([this.active$, this.authService.currentUser$]).pipe(
			map(([active, user]) => (active ? user?.account?.id ?? null : null)),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public get enabled(): boolean {
		return this.enabled$.value;
	}

	public setEnabled(enabled: boolean): void {
		this.enabled$.next(enabled);
		localStorage.setItem(STORAGE_KEY, String(enabled));
	}
}
